import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import axios from 'axios';

const FootballLiveScores = () => {
  const [activeTab, setActiveTab] = useState('live');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [liveMatches, setLiveMatches] = useState([]);
  const [scheduledMatches, setScheduledMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLowerLeagues, setShowLowerLeagues] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'live', label: 'Live', count: liveMatches.length },
    { id: 'finished', label: 'Finished' },
    { id: 'upcoming', label: 'Upcomfootballing' }
  ];

  const fetchLiveMatches = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/scores/live/football`);
      let filteredLiveMatches = response.data.events || [];
      
      if (!showLowerLeagues) {
        filteredLiveMatches = filteredLiveMatches.filter(match => 
          match.tournament.uniqueTournament?.primaryColorHex
        );
      }
      
      setLiveMatches(filteredLiveMatches);
    } catch (error) {
      console.error('Error fetching live matches:', error);
    }
  };

  const fetchScheduledMatches = async (date) => {
    try {
      const formattedDate = date.toISOString().split('T')[0];
      const response = await axios.get(`${BACKEND_URL}/api/scores/scheduled/football/${formattedDate}`);
      let filteredScheduledMatches = response.data.events || [];      
      setScheduledMatches(filteredScheduledMatches);
    } catch (error) {
      console.error('Error fetching scheduled matches:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchLiveMatches(),
        fetchScheduledMatches(selectedDate)
      ]);
      setLoading(false);
    };

    fetchData();
    
    // Refresh live data every 30 seconds
    const interval = setInterval(() => fetchLiveMatches(), 30000);
    return () => clearInterval(interval);
  }, [selectedDate, showLowerLeagues]);

  const handleDateChange = (increment) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + increment);
    setSelectedDate(newDate);
  };

  const getDisplayMatches = () => {
    switch (activeTab) {
      case 'live':
        return liveMatches;
      case 'finished':
        return scheduledMatches.filter(match => match.status.type === 'finished');
      case 'upcoming':
        return scheduledMatches.filter(match => match.status.type === 'notstarted');
      default:
        return [...liveMatches, ...scheduledMatches];
    }
  };

  const getTournamentLogo = (tournament) => {
    if (tournament?.uniqueTournament?.id) {
      return `https://api.sofascore.app/api/v1/unique-tournament/${tournament.uniqueTournament.id}/image`;
    }
    return '';
  };

  const getMatchTime = (match) => {
    if (match.status.type === 'finished') return 'FT';
    if (match.status.type === 'inprogress') {
      const startTime = new Date(match.time.currentPeriodStartTimestamp * 1000);
      const now = new Date();
      const minutes = Math.floor((now - startTime) / 60000);
      return `${minutes}'`;
    }
    return new Date(match.startTimestamp * 1000).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  return (
    <div className="w-full p-4">
      {/* Date Navigation */}
      <div className="flex items-center justify-between mb-4 text-white">
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => handleDateChange(-1)}
            className="p-1.5 text-black hover:text-white hover:bg-gray-700 rounded-full"
          >
            <ChevronLeft size={20} />
          </button>
          <button className="px-3 py-1.5  bg-gray-800 rounded-lg flex items-center space-x-2">
            <Calendar size={16} />
            <span>
              {selectedDate.toLocaleDateString('en-GB', { 
                day: '2-digit',
                month: '2-digit'
              })}
            </span>
          </button>
          <button 
            onClick={() => handleDateChange(1)}
            className="p-1.5 text-black hover:bg-gray-700 hover:text-white rounded-full"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <div>
          {/* <label className="flex items-center space-x-2">
            <input 
              type="checkbox" 
              checked={showLowerLeagues}
              onChange={() => setShowLowerLeagues(!showLowerLeagues)}
            />
            <span>Show Lower Leagues</span>
          </label> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded-full text-sm ${
              activeTab === tab.id ? 'bg-red-500' : 'hover:bg-gray-700 hover:text-white'
            }`}
          >
            {tab.label} {tab.count && `(${tab.count})`}
          </button>
        ))}
      </div>

      {/* Matches List */}
      {loading ? (
        <div className="text-center py-8">Loading matches...</div>
      ) : (
        <div className="space-y-4">
          {getDisplayMatches().map((match) => (
            <a 
              key={match.id} 
              href={`https://www.sofascore.com/football/match/${match.slug}/${match.customId}#id:${match.id}`} 
              target='_blank' 
              rel="noopener noreferrer"
            >
              <div className="rounded-2xl border border-gray-200 pt-3 pl-5 space-y-2 mb-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {match.tournament && (
                      <img
                        src={getTournamentLogo(match.tournament)}
                        alt={match.tournament.category.name}
                        className="w-5 h-5 rounded-full"
                      />
                    )}
                    <span className="text-sm text-black">{match.tournament.name}</span>
                  </div>
                </div>

                <div className="p-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="w-12 text-sm text-black-600">
                      {getMatchTime(match)}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center space-x-2">
                          <img
                            src={`https://img.sofascore.com/api/v1/team/${match.homeTeam.id}/image`}
                            alt={match.homeTeam.name}
                            className="w-4 h-4 object-contain"
                          />
                          <span className="font-medium">{match.homeTeam.shortName}</span>
                        </div>
                        <span className="font-bold">{match.homeScore?.current || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <img
                            src={`https://img.sofascore.com/api/v1/team/${match.awayTeam.id}/image`}
                            alt={match.awayTeam.name}
                            className="w-4 h-4 object-contain"
                          />
                          <span className="font-medium">{match.awayTeam.shortName}</span>
                        </div>
                        <span className="font-bold">{match.awayScore?.current || '-'}</span>
                      </div>
                    </div>
                    <div className="w-8 ml-4"></div>
                  </div>
                  <div className="mt-2">
                    <span className={`text-xs ${
                      match.status.type === 'inprogress' ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {
                      match.status.description === 'Ended'
                        ? new Intl.DateTimeFormat('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          }).format(new Date(match.changes?.changeTimestamp * 1000 || match.startTimestamp * 1000))
                        : (match.status.description === 'Not started'
                            ? new Intl.DateTimeFormat('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              }).format(new Date(match.startTimestamp * 1000))
                            : match.status.description)
                      }
                    </span>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default FootballLiveScores;